/**
 * Agent tool contracts (function-calling).
 *
 * Shared TS types for tool arguments/results. The functions-side agent module
 * translates these into @google/genai FunctionDeclarations; keeping them here
 * decouples shared from the GenAI SDK.
 */

import type { AccountSourceLabel } from './googleServices'
import type {
  ClientProfileDoc,
  ConnectedAccountSlotId,
  EpochMillis,
  MemoryCategory,
  MemoryDoc,
  MicrosoftConnectionStatus,
  RecurrenceRule,
  ReminderDoc,
} from './models'
import type { CalendarEvent, DateRange } from './services'

export const AGENT_TOOL_NAMES = [
  'getCalendar',
  'createReminder',
  'listReminders',
  'sendTeamsMessage',
  'getClientProfile',
  'upsertClientProfile',
  'webSearch',
  'searchMemories',
  'saveMemory',
  'completeOnboarding',
  'startEmailResearch',
  'searchEmails',
  'sendEmail',
  'replyEmail',
  'createEvent',
  'searchTenantUsers',
  'listConnectedAccounts',
  'sendWhatsappMessage',
  'sendWhatsappTemplateToContact',
] as const

export type AgentToolName = (typeof AGENT_TOOL_NAMES)[number]

export interface GetCalendarArgs {
  /** ISO 8601. If omitted, defaults chosen by the tool (e.g. today). */
  startISO?: string
  endISO?: string
  /** Convenience presets the model may use instead of explicit dates. */
  preset?: 'today' | 'tomorrow' | 'thisWeek' | 'next24h'
}
export interface GetCalendarResult {
  range: DateRange
  events: CalendarEvent[]
}

export interface CreateReminderArgs {
  title: string
  dueISO: string
  description?: string
  recurrence?: RecurrenceRule
  /** Marca o lembrete como urgente (ênfase no texto composto); não altera os canais de entrega. */
  urgent?: boolean
}
export type CreateReminderResult = Pick<ReminderDoc, 'id' | 'title' | 'dueAt' | 'status' | 'urgent'>

export interface ListRemindersArgs {
  status?: ReminderDoc['status']
  fromISO?: string
  toISO?: string
}
export interface ListRemindersResult {
  reminders: Array<Pick<ReminderDoc, 'id' | 'title' | 'dueAt' | 'status'>>
}

export interface SendTeamsMessageArgs {
  text: string
}
export interface SendTeamsMessageResult {
  messageId: string
}

export interface GetClientProfileArgs {
  /** Client name or company to look up. */
  query: string
}
export interface GetClientProfileResult {
  match: ClientProfileDoc | null
}

export interface UpsertClientProfileArgs {
  name: string
  company?: string
  sector?: string
  notes?: string
  matchKeywords?: string[]
}
export type UpsertClientProfileResult = Pick<ClientProfileDoc, 'id' | 'name'>

export interface WebSearchArgs {
  query: string
}
export interface WebSearchResult {
  summary: string
  sources: Array<{ title?: string; url: string }>
  /** Consultas executadas pelo Google Search durante o grounding. */
  queries?: string[]
  /** HTML do Search Entry Point (renderedContent) para exibição na UI. */
  searchEntryPointHtml?: string
}

export interface SearchMemoriesArgs {
  query?: string
  category?: MemoryCategory
  subject?: string
}
export interface SearchMemoriesResult {
  memories: Array<Pick<MemoryDoc, 'id' | 'category' | 'subject' | 'content' | 'updatedAt'>>
}

export interface SaveMemoryArgs {
  content: string
  category: MemoryCategory
  subject?: string
}
export type SaveMemoryResult = Pick<MemoryDoc, 'id' | 'status'>

export interface CompleteOnboardingArgs {
  memories: Array<{ category: MemoryCategory; subject?: string; content: string }>
}
export interface CompleteOnboardingResult {
  saved: number
  completedAt: EpochMillis
}

/**
 * Argumentos da pesquisa profunda de e-mails (operação custosa, roda em
 * background). O período (startISO/endISO) e o foco são obrigatórios; os demais
 * são filtros opcionais. `sendEmail` liga a entrega adicional do relatório na
 * própria caixa do usuário (exige o scope Mail.Send).
 */
export interface StartEmailResearchArgs {
  /** ISO 8601 — início do período analisado (inclusivo). */
  startISO: string
  /** ISO 8601 — fim do período analisado (inclusivo). */
  endISO: string
  /** Foco da análise em linguagem natural (o que priorizar). */
  focus: string
  /** Se presente, restringe aos e-mails destes remetentes (endereços). */
  allowedSenders?: string[]
  /** Filtra por termo contido no assunto. */
  subjectFilter?: string
  /** Exclui e-mails ENDEREÇADOS a estes endereços (to/cc); não filtra por remetente. */
  excludeRecipients?: string[]
  /**
   * Também enviar o relatório por e-mail para a própria caixa (Mail.Send).
   * Recurso opt-in sujeito a habilitação pelo administrador: se estiver
   * desativado, o pedido é ignorado e o relatório chega apenas na conversa.
   */
  sendEmail?: boolean
}

export interface StartEmailResearchResult {
  jobId: string
  status: 'queued'
  /**
   * Aviso pt-BR opcional para o modelo repassar ao usuário (ex.: quando o envio
   * por e-mail foi solicitado mas está desativado pelo administrador).
   */
  note?: string
}

/**
 * Argumentos da busca pontual de e-mails (leitura, sem confirmação). Todos os
 * filtros são opcionais; sem nenhum, retorna as mensagens mais recentes.
 */
export interface SearchEmailsArgs {
  /** Termo contido no ASSUNTO das mensagens. */
  query?: string
  /** Remetente: endereço exato ou parte do nome/endereço. */
  from?: string
  /** ISO 8601 — início do período (recebimento, inclusivo). */
  startISO?: string
  /** ISO 8601 — fim do período (recebimento, inclusivo). */
  endISO?: string
  /** Máximo de resultados (teto imposto pelo backend). */
  limit?: number
}
export interface SearchEmailsResultItem {
  /** Id da mensagem no Outlook — use-o em replyEmail. */
  id: string
  /** Nome (ou endereço) do remetente. */
  from: string
  /** Endereço do remetente, quando disponível. */
  fromAddress?: string
  subject: string
  /** Recebimento em epoch millis. */
  receivedAt: EpochMillis
  /** Prévia curta do corpo. */
  preview: string
  /** Link para abrir a mensagem no Outlook. */
  webLink?: string
  /**
   * Origem do item quando agregado de múltiplas contas conectadas (ver
   * googleServices.ts#AccountSourceLabel). Ausente ⇒ conta primária.
   */
  source?: AccountSourceLabel
}
export interface SearchEmailsResult {
  emails: SearchEmailsResultItem[]
}

/**
 * Argumentos do envio de um novo e-mail (Microsoft Graph POST /me/sendMail).
 * Ação de ESCRITA: exige confirmação explícita do usuário (ver MUTATING_TOOL_NAMES).
 * O corpo é texto simples (o backend envia como contentType 'Text').
 */
export interface SendEmailArgs {
  /** Destinatários (endereços de e-mail). Ao menos um. */
  to: string[]
  /** Destinatários em cópia (opcional). */
  cc?: string[]
  subject: string
  /** Corpo do e-mail em texto simples. */
  body: string
  /**
   * Conta de origem do envio ('primary' = Outlook da conta principal, ou o
   * slotId de uma conta conectada — ver ConnectedAccountSlotId). Ausente com
   * apenas UMA conta candidata ⇒ comportamento atual (zero regressão);
   * ausente com 2+ contas ⇒ erro controlado pedindo para o modelo escolher.
   */
  accountId?: 'primary' | ConnectedAccountSlotId
}
export interface SendEmailResult {
  sent: true
  to: string[]
}

/**
 * Argumentos da resposta a um e-mail existente (Graph POST
 * /me/messages/{id}/reply — ou /replyAll). Ação de ESCRITA: exige confirmação.
 * `messageId` é o id da mensagem do Outlook a ser respondida — obtenha-o via
 * searchEmails (campo `id` de cada resultado).
 */
export interface ReplyEmailArgs {
  /** Id da mensagem do Outlook a responder (campo `id` de searchEmails). */
  messageId: string
  /** Corpo da resposta em texto simples. */
  body: string
  /** Responder a todos os destinatários (default: só ao remetente). */
  replyAll?: boolean
  /** Conta de origem da resposta — mesma semântica de SendEmailArgs.accountId. */
  accountId?: 'primary' | ConnectedAccountSlotId
}
export interface ReplyEmailResult {
  replied: true
}

/**
 * Argumentos da criação de um evento na agenda do usuário (Microsoft Graph POST
 * /me/events). Ação de ESCRITA: exige confirmação explícita do usuário (ver
 * MUTATING_TOOL_NAMES). `startISO`/`endISO` são strings ISO 8601 (mesmo padrão
 * de `CreateReminderArgs.dueISO`) — o modelo não precisa calcular epoch millis,
 * o que é propenso a erro. Por padrão o evento é criado como reunião online do
 * Teams (isOnlineMeeting = true), devolvendo o joinUrl; passe
 * isOnlineMeeting: false para um compromisso sem link do Teams.
 */
export interface CreateEventArgs {
  /** Título do evento. */
  subject: string
  /** Início do evento em ISO 8601, com offset (ex.: 2026-07-14T15:00:00-03:00). */
  startISO: string
  /** Fim do evento em ISO 8601, com offset (ex.: 2026-07-14T16:00:00-03:00). */
  endISO: string
  /** Participantes (endereços de e-mail) convidados. */
  attendees?: string[]
  /** Descrição/pauta do evento (texto simples). */
  body?: string
  /** Local do evento (sala, endereço). */
  location?: string
  /** Criar como reunião online do Teams (default true). */
  isOnlineMeeting?: boolean
  /** Conta de origem do evento — mesma semântica de SendEmailArgs.accountId. */
  accountId?: 'primary' | ConnectedAccountSlotId
}
export interface CreateEventResult {
  created: true
  /** Id do evento criado no Outlook. */
  id: string
  /** Link para abrir o evento no Outlook. */
  webLink?: string
  /** URL de entrada da reunião do Teams, quando criada como reunião online. */
  onlineMeetingUrl?: string
}

/**
 * Argumentos da pesquisa de usuários no diretório da organização (Microsoft
 * Graph GET /users). Ação de LEITURA (sem confirmação). Requer o scope
 * User.ReadBasic.All (admin consent — ver MS_GRAPH_SCOPES).
 */
export interface SearchTenantUsersArgs {
  /** Termo de busca por nome ou e-mail. */
  query: string
  /** Máximo de resultados (teto imposto pelo backend). */
  limit?: number
}
export interface SearchTenantUsersResultItem {
  /** Id do usuário no diretório (Entra ID). */
  id: string
  displayName: string
  /** E-mail principal, quando disponível. */
  mail?: string
  /** Cargo, quando disponível. */
  jobTitle?: string
  /** Departamento/área, quando disponível. */
  department?: string
}
export interface SearchTenantUsersResult {
  users: SearchTenantUsersResultItem[]
}

/**
 * Resultado da tool listConnectedAccounts (leitura, sem confirmação): lista a
 * conta primária (Outlook/login) e cada conta ADICIONAL conectada, com sua
 * capacidade (agenda/e-mail), para o modelo escolher `accountId` em ações de
 * escrita ambíguas (ver SendEmailArgs.accountId).
 */
export interface ListConnectedAccountsResult {
  accounts: Array<{
    id: 'primary' | ConnectedAccountSlotId
    provider: 'microsoft' | 'google'
    email?: string
    status: MicrosoftConnectionStatus
    capabilities: Array<'calendar' | 'mail'>
  }>
}

/**
 * Argumentos do envio de mensagem por WhatsApp (BETA, via GHL). Ação de
 * ESCRITA: exige confirmação explícita do usuário (ver MUTATING_TOOL_NAMES).
 *
 * SEM destinatário de propósito: esta tool envia SEMPRE para o número de
 * WhatsApp VINCULADO do PRÓPRIO usuário (UserDoc.whatsapp), nunca para
 * terceiros — não há (e não deve haver) um campo de telefone/contato aqui.
 * O backend (functions/src/agent/tools.ts#executeTool) resolve o contactId a
 * partir do usuário autenticado; ver as pré-condições lá (feature habilitada,
 * número vinculado, janela de 24h aberta).
 */
export interface SendWhatsappMessageArgs {
  text: string
}
export interface SendWhatsappMessageResult {
  sent: true
  /**
   * `true` quando a entrega usou o template aprovado (`sendTemplate`, janela
   * de 24h fechada) em vez de texto livre (`sendText`, janela aberta).
   * Ausente/`undefined` equivale a `false` (texto livre — caso comum).
   */
  viaTemplate?: true
}

/**
 * Argumentos do disparo do template de WhatsApp aprovado para um CONTATO DO CRM
 * (GHL) — BETA. Ação de ESCRITA: exige confirmação explícita do usuário (ver
 * MUTATING_TOOL_NAMES). Distinta de `sendWhatsappMessage` (que envia SEMPRE
 * para o próprio número vinculado do usuário): aqui o destinatário é um
 * terceiro (contato do CRM), então:
 *  - `contactQuery` é só um TERMO DE BUSCA (nome, telefone ou e-mail) — nunca
 *    um contactId. O backend (functions/src/agent/tools.ts#executeTool)
 *    resolve o contactId no SERVIDOR via `GhlServices.searchContacts`; o
 *    modelo nunca escolhe/injeta o contactId diretamente.
 *  - `text` vira SEMPRE a variável `{{1}}` do template aprovado — nunca é
 *    enviado como texto livre a um terceiro (decisão de produto).
 * Ambiguidade (0 ou 2+ contatos encontrados) é um erro CONTROLADO: o modelo
 * deve perguntar ao usuário e reemitir a chamada com uma busca mais específica,
 * nunca escolher um contato sozinho.
 */
export interface SendWhatsappTemplateToContactArgs {
  /** Termo de busca do contato no CRM: nome, telefone ou e-mail. */
  contactQuery: string
  /** Conteúdo que preenche a variável {{1}} do template aprovado. */
  text: string
  /**
   * USO INTERNO — NÃO preencher; NÃO exposto na declaração enviada ao modelo
   * (agent-declarations.ts). O backend injeta este campo em `confirmation.args`
   * ao montar o cartão (com o `contactId` já resolvido pela busca), e ele volta
   * intacto na chamada confirmada (o cliente reenvia `toolArgs` sem alterar).
   * Na execução confirmada, o backend RE-RESOLVE a busca (mesma query) e exige
   * `resolved.contactId === expectedContactId`; um descompasso (contato
   * renomeado/removido/recriado entre o cartão e a confirmação) vira um erro
   * controlado em vez de enviar para um destinatário diferente do anunciado.
   * Uma injeção deste campo pelo próprio modelo é INÓCUA por design: o envio
   * SEMPRE usa o `contactId` resolvido nesta chamada pela busca no servidor —
   * `expectedContactId` só pode ADICIONAR uma falha (mismatch), nunca decidir
   * ou substituir o destino real do envio.
   */
  expectedContactId?: string
}
export interface SendWhatsappTemplateToContactResult {
  sent: true
  /** Nome do contato resolvido no CRM, para o modelo confirmar ao usuário. */
  contactName: string
  /** Sempre `true`: esta tool SÓ entrega via template aprovado. */
  viaTemplate: true
}

/** Uniform tool result envelope returned to the model. */
export type ToolResult<T> = { ok: true; data: T } | { ok: false; error: string }

/**
 * Tools que MUTAM estado e por isso só podem executar com confirmação explícita
 * do usuário (a trava é imposta NO CÓDIGO — ver functions executeTool). Estenda
 * esta lista ao adicionar novas tools de escrita, como as futuras tools de
 * agenda/reunião que criem ou alterem eventos.
 */
export const MUTATING_TOOL_NAMES = [
  'createReminder',
  'sendTeamsMessage',
  'upsertClientProfile',
  'saveMemory',
  'completeOnboarding',
  // Operação custosa (pipeline LLM + Graph); a confirmação protege o custo.
  'startEmailResearch',
  // Enviam/respondem e-mail em nome do usuário — escritas que saem para terceiros.
  'sendEmail',
  'replyEmail',
  // Cria evento/reunião na agenda do usuário — escrita que pode convidar terceiros.
  'createEvent',
  // BETA: envia mensagem pelo WhatsApp (GHL) — sempre para o próprio número
  // vinculado do usuário, nunca para terceiros; ainda assim é uma escrita que
  // sai para fora do sistema (chega no celular do usuário via WhatsApp).
  'sendWhatsappMessage',
  // BETA: dispara o template de WhatsApp aprovado para um CONTATO DO CRM
  // (terceiro) — escrita que sai para fora do sistema E para fora do próprio
  // usuário; confirmação obrigatória mostra o destinatário exato.
  'sendWhatsappTemplateToContact',
] as const satisfies readonly AgentToolName[]

/** True quando a tool muta estado e portanto exige confirmação explícita. */
export function isMutatingTool(name: AgentToolName): boolean {
  return (MUTATING_TOOL_NAMES as readonly AgentToolName[]).includes(name)
}

/** Entrada do catálogo de ferramentas exibido na UI (id + textos pt-BR). */
export interface AgentToolCatalogEntry {
  /** Id estável da ferramenta (casa com AgentToolName e o dispatcher). */
  id: AgentToolName
  /** Nome curto de exibição em pt-BR. */
  label: string
  /** Descrição curta em pt-BR do que a ferramenta faz. */
  description: string
}

/**
 * Catálogo das ferramentas que o usuário pode HABILITAR/DESABILITAR por conversa.
 * Fonte única dos rótulos para a UI (evita duplicar strings). Os ids casam com
 * AGENT_TOOL_NAMES / o dispatcher em functions (tools.ts#executeTool).
 *
 * `completeOnboarding` é OMITIDA de propósito: é um mecanismo interno da
 * apresentação inicial, não uma capacidade que o usuário deva alternar (se
 * desabilitada, a apresentação nunca concluiria).
 */
export const AGENT_TOOL_CATALOG: readonly AgentToolCatalogEntry[] = [
  {
    id: 'getCalendar',
    label: 'Agenda',
    description: 'Consultar os compromissos da agenda do Outlook.',
  },
  {
    id: 'createReminder',
    label: 'Criar lembrete',
    description: 'Criar lembretes entregues no Teams no vencimento.',
  },
  {
    id: 'listReminders',
    label: 'Listar lembretes',
    description: 'Consultar seus lembretes existentes.',
  },
  {
    id: 'sendTeamsMessage',
    label: 'Enviar no Teams',
    description: 'Enviar uma mensagem para você no Microsoft Teams.',
  },
  {
    id: 'getClientProfile',
    label: 'Consultar cliente',
    description: 'Buscar o perfil de um cliente cadastrado.',
  },
  {
    id: 'upsertClientProfile',
    label: 'Salvar cliente',
    description: 'Criar ou atualizar o perfil de um cliente.',
  },
  {
    id: 'webSearch',
    label: 'Pesquisa na web',
    description: 'Pesquisar fatos externos e recentes no Google.',
  },
  {
    id: 'searchMemories',
    label: 'Buscar memórias',
    description: 'Consultar suas memórias aprovadas.',
  },
  {
    id: 'saveMemory',
    label: 'Salvar memória',
    description: 'Guardar uma memória persistente a seu pedido.',
  },
  {
    id: 'startEmailResearch',
    label: 'Pesquisa de e-mails',
    description: 'Análise profunda dos e-mails de um período (operação demorada).',
  },
  {
    id: 'searchEmails',
    label: 'Buscar e-mails',
    description: 'Localizar mensagens na caixa de entrada por termo, remetente ou período.',
  },
  {
    id: 'sendEmail',
    label: 'Enviar e-mail',
    description: 'Enviar um novo e-mail pelo Outlook (requer confirmação).',
  },
  {
    id: 'replyEmail',
    label: 'Responder e-mail',
    description: 'Responder a um e-mail existente (requer confirmação).',
  },
  {
    id: 'createEvent',
    label: 'Criar evento',
    description:
      'Criar um evento na agenda, opcionalmente como reunião do Teams (requer confirmação).',
  },
  {
    id: 'searchTenantUsers',
    label: 'Pesquisar pessoas',
    description: 'Localizar pessoas da organização por nome ou e-mail.',
  },
  {
    id: 'listConnectedAccounts',
    label: 'Contas conectadas',
    description: 'Listar as contas de e-mail e agenda conectadas.',
  },
  {
    id: 'sendWhatsappMessage',
    label: 'WhatsApp (beta)',
    description:
      'BETA: enviar uma mensagem para o seu próprio WhatsApp vinculado (requer confirmação e canal configurado).',
  },
  {
    id: 'sendWhatsappTemplateToContact',
    label: 'WhatsApp para contato (beta)',
    description:
      'BETA: disparar o template aprovado de WhatsApp para um contato do CRM (requer confirmação e acesso liberado pelo administrador).',
  },
]

/**
 * True quando a ferramenta está HABILITADA para a conversa. `disabledTools`
 * ausente/vazio ⇒ todas habilitadas. Helper puro — consumido pela web e pelas
 * functions para filtrar a disponibilidade das tools por conversa.
 */
export function isToolEnabled(name: string, disabledTools?: readonly string[]): boolean {
  return !disabledTools?.includes(name)
}

/**
 * Normaliza o `disabledTools` lido do doc da conversa. O campo vem de escrita
 * DIRETA do cliente (as rules não validam o shape), então um valor não-array
 * (número/objeto) ou itens não-string quebrariam `includes`. Valor inválido
 * degrada para [] (nenhuma tool desabilitada). Helper puro — chame UMA vez no
 * ponto de leitura da conversa, antes de propagar.
 */
export function normalizeDisabledTools(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

/** Ação de escrita proposta, aguardando confirmação explícita do usuário. */
export interface ToolConfirmation {
  tool: AgentToolName
  args: Record<string, unknown>
  /** Resumo em pt-BR da ação exata que será executada ao confirmar. */
  summary: string
}

/**
 * Retorno de executeTool quando uma tool de escrita é chamada SEM confirmação:
 * nada é executado. O consumidor deve confirmar com o usuário e chamar de novo
 * com `confirmed: true`. Discrimine por `needsConfirmation`.
 *
 * `error` carrega um texto pt-BR de fallback para consumidores que ainda não
 * tratam `needsConfirmation` — eles degradam tratando a chamada como tool não
 * executada (o envelope permanece atribuível a ToolResult).
 */
export interface ToolConfirmationRequired {
  ok: false
  error: string
  needsConfirmation: true
  confirmation: ToolConfirmation
}

export interface AgentRunResult {
  messageId: string
  finished: boolean
}
